import {
  AssistantAppointmentHistoryRow,
  AssistantPart,
} from '@services/modules/AssistantStream'
import appStore from '@stores/appStore'
import { observer } from 'mobx-react'

/*
 * A case's appointment history, drawn from the rows the server sent - never from the answer's
 * text - with the columns and formats of the case screen's Appointment History
 * (ModalAppointmentHistory): the appointment in its own zone with the zone named, Updated Time as
 * that screen shows it. It is drawn inside the answer's markdown block, so Copy takes it with the
 * answer.
 */

export const APPOINTMENT_HISTORY_PART = 'appointment_history'

const COLUMNS = [
  'Appointment Info',
  'Appointment Accepts',
  'Status',
  'Updated By',
  'Updated Time',
]

const DATE_TIME_FORMAT = 'DD MMM YYYY HH:mm'

/** The server's times are UTC; one sent without an offset is read as UTC, not as local time. */
const asUtc = (value: string) =>
  /(Z|[+-]\d{2}:?\d{2})$/i.test(value) ? value : `${value}Z`

const formatted = (
  value: string,
  timeZone?: string,
  format = DATE_TIME_FORMAT
) => appStore.formatDateTime(asUtc(value), timeZone, format) ?? ''

/** "16 Nov 2026 08:00 -09:00 (AEDT, Sydney)", as the screen's Appointment Info shows the time. */
export const appointmentTimeText = (row: AssistantAppointmentHistoryRow) => {
  if (!row.start) {
    return ''
  }
  const start = formatted(row.start, row.timeZone)
  const end = row.end ? ` -${formatted(row.end, row.timeZone, 'HH:mm')}` : ''
  const zone = row.timeZone
    ? appStore.getTimeZoneDisplayName(asUtc(row.end ?? row.start), row.timeZone)
    : null
  return `${start}${end}${zone ? ` ${zone}` : ''}`
}

const infoLines = (row: AssistantAppointmentHistoryRow) =>
  [
    `Specialist: ${row.specialist ?? ''}`,
    `Centre: ${row.centre ?? ''}`,
    appointmentTimeText(row),
  ].filter(Boolean)

const statusLines = (row: AssistantAppointmentHistoryRow) =>
  [
    row.status ?? '',
    row.status === 'Cancelled' && row.cancellationReason
      ? `Reason: ${row.cancellationReason}`
      : '',
  ].filter(Boolean)

/** Updated Time as the screen shows it: no zone given, so in the user's own time. */
const updatedText = (row: AssistantAppointmentHistoryRow) =>
  row.updatedAt ? formatted(row.updatedAt) : ''

/** The table as markdown text, for the plain-text flavour of Copy. */
export const appointmentHistoryText = (part: AssistantPart): string => {
  const rows = part.appointments ?? []
  if (rows.length === 0) {
    return ''
  }
  const cell = (value: string) => value.replace(/\|/g, '\\|')
  const line = (cells: string[]) => `| ${cells.join(' | ')} |`
  return [
    line(COLUMNS),
    line(COLUMNS.map(() => '---')),
    ...rows.map(row =>
      line([
        cell(infoLines(row).join('; ')),
        cell(row.heldAs ?? ''),
        cell(statusLines(row).join('; ')),
        cell(row.updatedBy ?? ''),
        cell(updatedText(row)),
      ])
    ),
  ].join('\n')
}

const Lines = ({ lines }: { lines: string[] }) => (
  <>
    {lines.map((text, index) => (
      <div key={index}>{text}</div>
    ))}
  </>
)

const AppointmentHistoryPart = ({ part }: { part: AssistantPart }) => {
  const rows = part.appointments ?? []
  if (rows.length === 0) {
    return null
  }

  return (
    <div>
      <table>
        <thead>
          <tr>
            {COLUMNS.map(column => (
              <th key={column}>{column}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={index}>
              <td>
                <Lines lines={infoLines(row)} />
              </td>
              <td>{row.heldAs}</td>
              <td>
                <Lines lines={statusLines(row)} />
              </td>
              <td>{row.updatedBy}</td>
              <td>{updatedText(row)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export default observer(AppointmentHistoryPart)
